import {
  Box,
  Card,
  CardActions,
  CardHeader,
  Typography,
  Chip,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import RedditIcon from '@mui/icons-material/Reddit';

import get from '../api';

type PlatformKey = 'devto' | 'medium' | 'hackernoon' | 'reddit' | 'custom';

interface Source {
  platform: PlatformKey;
  value: string;
  label?: string;
}
interface DevToArticleInfo {
  id: number;
  title: string;
  comments_count: number;
  reading_time_minutes: number;
  readable_publish_date: string;
}
interface DevToArticleReactionInfo {
  category: string;
  count: number;
}
interface DevToArticleReactionsInfo {
  article_reaction_counts: Array<DevToArticleReactionInfo>;
}
interface DevToArticleCounters {
  savings_count: number;
  likes_count: number;
}

/* ---------- helpers ---------- */
function buildUrl(src: Source): string {
  switch (src.platform) {
    case 'devto':
      return `https://dev.to/${src.value.replace(/^\/+/, '')}`;
    case 'medium':
      return `https://medium.com/${src.value.replace(/^\/+/, '')}`;
    case 'hackernoon':
      return `https://hackernoon.com/${src.value.replace(/^\/+/, '')}`;
    case 'reddit':
      return `https://www.reddit.com/${src.value.replace(/^\/+/, '')}`;
    default:
      return src.value;
  }
}
function platformLabel(src: Source): string {
  if (src.label) return src.label;
  switch (src.platform) {
    case 'devto':
      return 'DEV.to';
    case 'medium':
      return 'Medium';
    case 'hackernoon':
      return 'HackerNoon';
    case 'reddit':
      return 'Reddit';
    default:
      return 'Link';
  }
}

// ——— палитра обводки баджей
const PLATFORM_COLORS: Record<PlatformKey, string> = {
  devto: '#6E7681', // спокойный серо-синий, хорошо читается
  medium: '#F2F2F2', // мягкий светло-серый-белый
  hackernoon: '#4CAF50', // гармоничный зелёный без кислотности
  reddit: '#FF5700', // фирменный оранжевый reddit
  custom: '#90A4AE', // нейтральный серо-голубой
};

const platformButtonSx = (p: PlatformKey) => {
  const c = PLATFORM_COLORS[p];
  return {
    textTransform: 'none',
    color: c,
    // color: '#fff',
    borderColor: c,
  };
};

// иконки из /public
const ICON_SRC: Record<PlatformKey, string> = {
  devto: '/devto.png',
  medium: '/medium.png',
  hackernoon: '/hackernoon.png',
  reddit: '', // svg из MUI, см. PlatformIcon
  custom: '',
};

// инвертируем ТОЛЬКО devto и medium → белые иконки на тёмном фоне
const ICON_FILTERS: Partial<Record<PlatformKey, string>> = {
  devto: 'invert(1) brightness(1.05) contrast(1.05)',
  medium: 'invert(1) brightness(1.05) contrast(1.05)',
  // hackernoon — без фильтра
};

function PlatformIcon({ platform }: { platform: PlatformKey }) {
  if (platform === 'reddit')
    return (
      <RedditIcon
        aria-hidden
        sx={{ fontSize: 16, display: 'block', color: 'white' }}
      />
    );
  const src = ICON_SRC[platform];
  if (!src) return null;
  return (
    <Box
      component="img"
      src={src}
      alt=""
      aria-hidden
      sx={{
        width: 16,
        height: 16,
        display: 'block',
        userSelect: 'none',
        pointerEvents: 'none',
        filter: ICON_FILTERS[platform] ?? 'none',
        m: 0, // без внутренних отступов
      }}
    />
  );
}

/* ——— Ровная, эстетичная мета-строка ——— */

// маленький аккуратный кружок-разделитель
const MetaSeparator = () => (
  <Box
    component="span"
    sx={{
      mx: 0.75,
      width: 3,
      height: 3,
      borderRadius: '50%',
      bgcolor: 'text.disabled',
      display: 'inline-block',
      alignSelf: 'center',
      flex: '0 0 auto',
    }}
  />
);

// единый стиль для элемента мета-инфо
function MetaItem({
  icon,
  children,
}: {
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        minWidth: 0,
      }}
    >
      {icon ? (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            mr: 0.5,
            '& svg': { fontSize: 16, color: 'text.secondary' },
          }}
        >
          {icon}
        </Box>
      ) : null}
      <Typography
        variant="body2"
        color="text.secondary"
        component="span"
        sx={{ lineHeight: 1.25 }}
      >
        {children}
      </Typography>
    </Box>
  );
}

/* ---------- component ---------- */
function ArticleCardUniversal({
  sources,
  titlePrefetch,
  readablePublishDate,
  readingTimeMinutes,
  likesCount,
  commentsCount,
  defaultPlatform = 'devto',
}: {
  sources: Source[];
  titlePrefetch?: string;
  readablePublishDate?: string; // как у dev.to: "Aug 28 '25"
  readingTimeMinutes?: number;
  likesCount?: number;
  commentsCount?: number;
  defaultPlatform?: PlatformKey;
}) {
  const [info, setInfo] = useState<DevToArticleInfo>({
    id: 0,
    title: titlePrefetch ?? 'Loading...',
    readable_publish_date:
      readablePublishDate ??
      (sources.some((s) => s.platform === 'devto') ? 'Loading...' : ''),
    comments_count: commentsCount ?? 0,
    reading_time_minutes: readingTimeMinutes ?? 0,
  });
  const [counters, setCounters] = useState<DevToArticleCounters>({
    savings_count: 0,
    likes_count: likesCount ?? 0,
  });

  const defaultIndex = useMemo(() => {
    const idx = sources.findIndex((s) => s.platform === defaultPlatform);
    return idx >= 0 ? idx : 0;
  }, [sources, defaultPlatform]);

  const [selectedIndex, setSelectedIndex] = useState<number>(defaultIndex);
  const devtoSource = useMemo(
    () => sources.find((s) => s.platform === 'devto'),
    [sources],
  );

  useEffect(() => {
    if (!devtoSource) return;
    const path = devtoSource.value.replace(/^\/+/, '');
    get(`https://dev.to/api/articles/${path}`)
      .then(({ data }: { data: DevToArticleInfo }) => {
        setInfo({
          id: data.id,
          title: data.title,
          readable_publish_date: data.readable_publish_date,
          comments_count: data.comments_count,
          reading_time_minutes: data.reading_time_minutes,
        });
        return data.id;
      })
      .then((articleId) =>
        get(`https://dev.to/reactions?article_id=${articleId}`).then(
          ({ data }: { data: DevToArticleReactionsInfo }) => {
            const readingList = data.article_reaction_counts.find(
              (i) => i.category === 'readinglist',
            );
            const reactionsToSum = [
              'like',
              'unicorn',
              'exploding_head',
              'raised_hands',
              'fire',
            ];
            const likesCount = data.article_reaction_counts
              .filter((i) => reactionsToSum.includes(i.category))
              .reduce((sum, cur) => sum + cur.count, 0);
            setCounters({
              savings_count: readingList?.count ?? 0,
              likes_count: likesCount,
            });
          },
        ),
      )
      .catch(() => {});
  }, [devtoSource]);

  useEffect(() => {
    if (!devtoSource && !titlePrefetch) {
      const url = buildUrl(sources[selectedIndex]);
      try {
        const u = new URL(url);
        setInfo((prev) => ({
          ...prev,
          title:
            decodeURIComponent(u.pathname.replace(/^\/+/, '')) || u.hostname,
        }));
      } catch {
        setInfo((prev) => ({ ...prev, title: url }));
      }
    }
  }, [devtoSource, titlePrefetch, selectedIndex, sources]);

  const [width, setWidth] = useState<number>(window.innerWidth);
  useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  const isMobile = width < 768;
  const cardActionsDirection = isMobile ? 'column-reverse' : 'row';

  const openSource = (idx: number) => {
    setSelectedIndex(idx);
    window.open(buildUrl(sources[idx]), '_blank', 'noopener,noreferrer');
  };

  // собираем метрики+мету
  const metaParts: React.ReactNode[] = [];
  if (counters.likes_count > 0)
    metaParts.push(
      <MetaItem icon={<FavoriteBorderIcon />}>{counters.likes_count}</MetaItem>,
    );
  if (counters.savings_count > 0)
    metaParts.push(
      <MetaItem icon={<BookmarkBorderIcon />}>
        {counters.savings_count}
      </MetaItem>,
    );
  if (info.comments_count > 0)
    metaParts.push(
      <MetaItem icon={<ForumOutlinedIcon />}>{info.comments_count}</MetaItem>,
    );
  if (info.reading_time_minutes)
    metaParts.push(
      <MetaItem>{`${info.reading_time_minutes} min read`}</MetaItem>,
    );
  if (info.readable_publish_date)
    metaParts.push(<MetaItem>{info.readable_publish_date}</MetaItem>);

  return (
    <Card
      sx={{
        mb: 3,
        px: 3,
        py: 2,
        borderRadius: 2,
        border: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <CardHeader
        sx={{ p: 0, mb: 1.5 }}
        title={
          <Typography variant="body1">
            {info.title}
          </Typography>
        }
      />

      <CardActions
        sx={{
          display: 'flex',
          flexDirection: cardActionsDirection,
          p: 0,
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flex: '1 1 auto',
            minWidth: 0,
            alignItems: 'center',
            alignSelf: 'start',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
          }}
        >
          {sources.map((src, idx) => (
            <Chip
              key={`${src.platform}-${idx}`}
              variant="outlined"
              size="small"
              clickable
              onClick={() => openSource(idx)}
              icon={<PlatformIcon platform={src.platform} />}
              label={platformLabel(src)}
              sx={{
                ...platformButtonSx(src.platform),
                border: `1px solid ${PLATFORM_COLORS[src.platform]}`,
                fontSize: '0.85rem',
                px: 0.75,
                height: 28,
                borderRadius: 9999, // пилюля
                '& .MuiChip-icon': { ml: 0.5, mr: 0.5 },
                '& .MuiChip-icon > *': { m: 0 },
                // '&:hover': { bgcolor: 'action.hover' },
                '&:hover': { bgcolor: 'action.selected' },
                '&.Mui-focusVisible': {
                  boxShadow: '0 0 0 3px rgba(255,255,255,0.06)',
                },
              }}
            />
          ))}
        </Box>
      </CardActions>

      {metaParts.length ? (
        <Box
          sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap', // одна строка
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {metaParts.map((node, i) => (
            <React.Fragment key={i}>
              {i > 0 && <MetaSeparator />}
              {node}
            </React.Fragment>
          ))}
        </Box>
      ) : null}
    </Card>
  );
}

function Articles() {
  return (
    <Box>
      <ArticleCardUniversal
        titlePrefetch="Type Inference Is Hard. I Made It Harder, Then I Made It Work"
        readablePublishDate="Aug 13 '26"
        readingTimeMinutes={7}
        likesCount={59}
        commentsCount={5}
        sources={[
          {
            platform: 'reddit',
            value:
              'r/ProgrammingLanguages/comments/1vnbf0v/type_inference_is_hard_i_made_it_harder_then_i',
          },
        ]}
      />
      <ArticleCardUniversal
        sources={[
          {
            platform: 'devto',
            value:
              'alinsky/solid-principles-in-2025-in-practice-with-python-and-uml-examples-34l6',
          },
          {
            platform: 'medium',
            value:
              '@alinsky/solid-principles-in-2025-in-practice-with-python-and-uml-examples-a7db14c89266',
          },
          {
            platform: 'hackernoon',
            value:
              'solid-principles-in-practice-with-python-and-uml-examples-in-2025',
          },
        ]}
      />
      <ArticleCardUniversal
        sources={[
          {
            platform: 'devto',
            value:
              'alinsky/automation-scheduling-python-programs-pushing-notifications-executing-sqls-etc-4ob0',
          },
          {
            platform: 'medium',
            value:
              '@alinsky/automation-scheduling-python-programs-pushing-notifications-executing-sqls-etc-c0c06bd3a4d7',
          },
          {
            platform: 'hackernoon',
            value: 'mastering-python-script-scheduling-pitfalls-and-solutions',
          },
        ]}
      />
    </Box>
  );
}

export default Articles;
